import SectionNavigator from "@/components/sectionNavigator";
import { apiGetRequest, apiPutRequest, logCourseAction } from "@/functions/requests";
import { useApp, useMedia, useProblem, useS3Client } from "@/hooks/contextHooks";
import { Flex, Text, Box, useColorModeValue, Heading, Center, useToast } from "@chakra-ui/react";
import { AppMeta, Content, ContentReference, HotSpot, NewContentItem, PixelCoordinate, Polygon, Section, SectionMeta, NewSection } from "@xnav/types/src/common";
import { GetObjectCommand } from "@aws-sdk/client-s3";
import { getSignedUrl } from "@aws-sdk/s3-request-presigner";
import { useEffect, useRef, useState, useMemo } from "react";
import ContentEditor from "@/components/contentEditor";
import { SelectedMarkup } from "@/components/videoMarkupEditor";
import SectionEditor from "@/components/sectionEditor";
import { ContentStoreContext, SetContentStoreContext, updateGlobalContentStore, DereferenceMediaContext, EditorSyncContext, ReferenceMediaContext, SectionStoreContext, SelectionReferenceContext, UploadJobsContext, DeletedContentContext, DeletedSectionsContext, StructureDirtyContext, ContentStructureContext, NavCollapsedContext } from "@/contexts/contexts";
import { createServerContent, createClientContent } from "@/functions/editorFunctions";
import { ReactStateType } from "@xnav/types/src/frontend";
import { MediaMeta } from "./mediaManager";
import { useSearchParams } from "react-router-dom";
import { useAuthenticator } from "@aws-amplify/ui-react";
import PdfEditor from "@/components/pdfEditor";
import AudioEditor from "@/components/audioEditor";
import QuizEditor from "@/components/quizEditor";
import ImageEditor from "@/components/imageEditor";
import FlashcardEditor from "@/components/flashcardEditor";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faVideo } from "@fortawesome/free-solid-svg-icons";

export type EditPolygon = Polygon & { midPoints: PixelCoordinate[], noFill?: boolean, isPreMade?: boolean, rotation?: number, thickness?: number, isCircle?: boolean, keepAspectRatio?: boolean, titlePosition?: 'top' | 'bottom' | 'left' | 'right' | 'center', titleOffset?: number, showLeaderLine?: boolean, isText?: boolean, fontSize?: number, fontFamily?: string, borderStyle?: 'solid' | 'dashed' | 'pill' | 'none', textEffect?: 'none' | 'shadow' | 'glow' }

export type EditHotSpot = HotSpot & object

export type EditContent = Content & {
	polygons: EditPolygon[],
	hotSpots: EditHotSpot[],
	selectedMarkup?: SelectedMarkup,
	clientScale: number
}

export enum SelectionFocus {
	content = 0,
	section = 1
}

export type SelectionType = ContentReference & {
	type: SelectionFocus
}

export type ContentStructureRequest = {
	newStructure: SectionMeta[],
	deletedContent: string[],
	deletedSections: string[]
}


export default function Editor() {
	const { user } = useAuthenticator()
	const app = useApp()
	const toast = useToast()
	const [editorSync, setEditorSync] = useState(false)
	const [isNavCollapsed, setIsNavCollapsed] = useState(false)
	const uploadJobs = useRef(0)

	function pushJob() {
		uploadJobs.current += 1
	}
	function popJob() {
		uploadJobs.current -= 1
		if (uploadJobs.current === 0) setEditorSync(false)
	}

	const [selectionReference, setSelectionReference] = useState<NewContentItem>()
	const [searchParams, setSearchParams] = useSearchParams()
	const [originalContents, setOriginalContents] = useState<Record<string, string>>({})

	const [contentStore, setContentStore] = useState<Record<string, EditContent>>({})

	useEffect(() => {
		updateGlobalContentStore(contentStore);
		if (Object.keys(contentStore).length === 0) {
			setOriginalContents({})
			return
		}
		setOriginalContents(prev => {
			let changed = false
			const next = { ...prev }
			Object.entries(contentStore).forEach(([id, content]) => {
				if (!next[id] && id && id !== 'undefined' && id !== 'null') {
					next[id] = JSON.stringify(createServerContent(content))
					changed = true
				}
			})
			return changed ? next : prev
		})
	}, [contentStore])

	const [sectionStore, setSectionStore] = useState<Record<string, Section>>({})

	const [, setProblem] = useProblem()
	const s3 = useS3Client()

	const [isSynced, setIsSynced] = useState<boolean>(true)
	const stayUnsynced = useRef(false)


	const [media, setMedia] = useMedia() as ReactStateType<MediaMeta[]>
	const [deletedContent, setDeletedContent] = useState<string[]>([])
	const [deletedSections, setDeletedSections] = useState<string[]>([])
	const [structureDirty, setStructureDirty] = useState<boolean>(false)
	const [sectionMeta, setSectionMeta] = useState<NewSection[] | undefined>(undefined)

	function referenceMedia(mediaId: string) {
		if (!selectionReference) return
		setMedia(media => {
			return media.map(m => m.id === mediaId ? {
				...m,
				references: [...m.references, selectionReference.id]
			} : m)
		})
	}

	// we need to filter only one instance
	function dereferenceMedia(mediaId: string) {
		if (!selectionReference) return
		setMedia(media => {
			const mediaItem = media.find(m => m.id === mediaId)!
			const index = mediaItem.references.findIndex(r => r === selectionReference.id)
			const newMedia: MediaMeta = {
				...mediaItem,
				references: [
					...mediaItem.references.slice(0, index),
					...mediaItem.references.slice(index + 1)
				]
			}
			return media.map(m => m.id === mediaId ? newMedia : m)
		})
	}
	const hasContentChanges = useMemo(() => {
		return Object.entries(contentStore).some(([id, content]) => {
			const original = originalContents[id];
			if (!original) return false;
			const currentStr = JSON.stringify(createServerContent(content));
			return currentStr !== original;
		});
	}, [contentStore, originalContents]);

	const hasUnsavedChanges =
		hasContentChanges ||
		Object.keys(sectionStore).length > 0 ||
		deletedContent.length > 0 ||
		deletedSections.length > 0 ||
		structureDirty;

	useEffect(() => {
		window.dispatchEvent(new CustomEvent('editor-dirty-state', { detail: { isDirty: hasUnsavedChanges } }));
	}, [hasUnsavedChanges]);

	useEffect(() => {
		window.dispatchEvent(new CustomEvent('editor-saving-state', { detail: { isSaving: editorSync } }));
	}, [editorSync]);

	useEffect(() => {
		if (editorSync) syncEditor()
	}, [editorSync])

	useEffect(() => {
		const handleSave = () => {
			setEditorSync(true);
		};
		const handleCancel = () => {
			setSelectionReference(undefined);
			setContentStore({});
			setOriginalContents({});
			setSectionStore({});
			setDeletedContent([]);
			setDeletedSections([]);
			setStructureDirty(false);
			const fetchContentStructure = async () => {
				try {
					const data = await apiGetRequest<string, NewSection[]>(`/structure/${app.id}`);
					setSectionMeta(data);
				} catch {
					setSectionMeta([]);
				}
			};
			fetchContentStructure();
			(setMedia as any)(undefined);
		};
		const handleQuiz = () => {
			setSelectionReference({ id: `${app.id}-quiz`, title: "Course Quiz", type: 'quiz' } as any);
		};
		const handleFlashcards = () => {
			setSelectionReference({ id: `${app.id}-flashcard`, title: "Course Flashcards", type: 'flashcard' } as any);
		};

		window.addEventListener('editor-save', handleSave);
		window.addEventListener('editor-cancel', handleCancel);
		window.addEventListener('editor-quiz', handleQuiz);
		window.addEventListener('editor-flashcards', handleFlashcards);

		return () => {
			window.removeEventListener('editor-save', handleSave);
			window.removeEventListener('editor-cancel', handleCancel);
			window.removeEventListener('editor-quiz', handleQuiz);
			window.removeEventListener('editor-flashcards', handleFlashcards);
		};
	}, [app.id]);

	useEffect(() => {
		if (selectionReference && sectionMeta) {
			const path = findPath(sectionMeta, selectionReference.id);
			console.log(`[OUTLINE TRAVERSE TRACE] Path to selected content: ${path ? path.join(' -> ') : 'Not Found'}`);
		}
	}, [selectionReference, sectionMeta]);

	const initialSelectChecked = useRef(false);
	useEffect(() => {
		const selectId = searchParams.get('select');
		if (selectId && sectionMeta && !initialSelectChecked.current) {
			const found = findContentItem(sectionMeta, selectId);
			if (found) {
				setSelectionReference(found);
				initialSelectChecked.current = true;
			}
		}
	}, [searchParams, sectionMeta]);

	useEffect(() => {
		if (selectionReference?.id) {
			const currentSelect = searchParams.get('select');
			if (currentSelect !== selectionReference.id) {
				setSearchParams({ select: selectionReference.id }, { replace: true });
			}
		}
	}, [selectionReference?.id]);

	useEffect(() => {
		if (selectionReference && sectionMeta) {
			const path = findPath(sectionMeta, selectionReference.id);
			if (path) {
				window.dispatchEvent(new CustomEvent('editor-selection-changed', { detail: { path } }));
			} else {
				window.dispatchEvent(new CustomEvent('editor-selection-changed', { detail: { path: [] } }));
			}
		} else {
			window.dispatchEvent(new CustomEvent('editor-selection-changed', { detail: { path: [] } }));
		}
	}, [selectionReference, sectionMeta]);

	useEffect(() => {
		return () => {
			window.dispatchEvent(new CustomEvent('editor-selection-changed', { detail: { path: [] } }));
		};
	}, []);

	async function syncEditor() {
		pushJob()
		const apiRequests: Promise<any>[] = []
		
		// Update app edited timestamp in database
		const updatedApp = {
			...app,
			edited: new Date().toString()
		}
		apiRequests.push(
			apiPutRequest<AppMeta, undefined>(`/apps/${app.id}`, updatedApp)
		)

		// Push content store (filter out invalid or undefined keys)
		Object.entries(contentStore).forEach(([id, content]) => {
			if (!id || id === 'undefined' || id === 'null') return;
			apiRequests.push(
				apiPutRequest<Content, undefined>(`/content/${app.id}/${id}`, createServerContent(content))
			)
		})
		// Push section store (filter out invalid or undefined keys)
		Object.entries(sectionStore).forEach(([id, section]) => {
			if (!id || id === 'undefined' || id === 'null') return;
			apiRequests.push(
				apiPutRequest<Section, undefined>(`/section/${app.id}/${id}`, section)
			)
		})

		// Dynamic Reference Recalculation
		try {
			if (media && Array.isArray(media)) {
				const newMediaReferences: Record<string, Set<string>> = {}
				const videoContentIds: string[] = []

				const traverse = (node: any) => {
					if (!node) return
					if (node.type === 'section') {
						if (node.iconId) {
							if (!newMediaReferences[node.iconId]) newMediaReferences[node.iconId] = new Set()
							newMediaReferences[node.iconId].add(node.id)
						}
						if (node.contents) {
							node.contents.forEach(traverse)
						}
					} else {
						if (node.type === 'video') {
							videoContentIds.push(node.id)
						}
						if (node.fileId) {
							if (!newMediaReferences[node.fileId]) newMediaReferences[node.fileId] = new Set()
							newMediaReferences[node.fileId].add(node.id)
						}
						if (node.videoId) {
							if (!newMediaReferences[node.videoId]) newMediaReferences[node.videoId] = new Set()
							newMediaReferences[node.videoId].add(node.id)
						}
					}
				}

				if (sectionMeta) {
					sectionMeta.forEach(traverse)
				}

				const contentPromises = videoContentIds.map(async (vId) => {
					if (!vId || vId === 'undefined') return;
					const contentItem = contentStore[vId]
					if (contentItem) {
						const videoId = contentItem.videoId
						if (videoId) {
							if (!newMediaReferences[videoId]) newMediaReferences[videoId] = new Set()
							newMediaReferences[videoId].add(vId)
						}
						const wisdomDocTitles = contentItem.wisdomDocTitles || []
						wisdomDocTitles.forEach((docId: string) => {
							if (!newMediaReferences[docId]) newMediaReferences[docId] = new Set()
							newMediaReferences[docId].add(vId)
						})
					} else {
						try {
							const command = new GetObjectCommand({
								Bucket: import.meta.env.VITE_EDIT_BUCKET_NAME,
								Key: `${app.id}/contents/content/${vId}.json`
							})
							const url = await getSignedUrl(s3 as any, command as any, { expiresIn: 360 })
							const response = await fetch(url)
							if (response.ok) {
								const strData = await response.text()
								let parsed = JSON.parse(strData)
								if (typeof parsed === 'string') parsed = JSON.parse(parsed)
								const client = createClientContent(parsed)
								const videoId = client?.videoId || parsed?.videoId
								if (videoId) {
									if (!newMediaReferences[videoId]) newMediaReferences[videoId] = new Set()
									newMediaReferences[videoId].add(vId)
								}
								const wisdomDocTitles = client?.wisdomDocTitles || parsed?.wisdomDocTitles || []
								wisdomDocTitles.forEach((docId: string) => {
									if (!newMediaReferences[docId]) newMediaReferences[docId] = new Set()
									newMediaReferences[docId].add(vId)
								})
							}
						} catch (e) {
							console.error("Failed to fetch content item during reference rebuild:", vId, e)
						}
					}
				})

				await Promise.all(contentPromises)

				const updatedMediaList = media.map((mediaRes: MediaMeta) => {
					const refs = newMediaReferences[mediaRes.id]
					return {
						...mediaRes,
						references: refs ? [...refs] : []
					}
				})

				apiRequests.push(
					apiPutRequest<MediaMeta[], undefined>(`/medias/${app.id}`, updatedMediaList)
						.then(() => setMedia(updatedMediaList))
				)
			}
		} catch (e) {
			console.error("Failed to recalculate media references on save:", e)
		}

		// Log Course Action
		const contentNames = Object.values(contentStore).map(c => {
			if (!c) return null;
			const typeParts: string[] = [];
			if (c.polygons && c.polygons.length > 0) typeParts.push("annotations");
			if (c.hotSpots && c.hotSpots.length > 0) typeParts.push("hotspots");
			if (c.quizData && c.quizData.questions && c.quizData.questions.length > 0) typeParts.push("quiz");
			if (c.flashcardData && c.flashcardData.quests && c.flashcardData.quests.length > 0) typeParts.push("flashcards");
			const typeStr = typeParts.length > 0 ? ` (${typeParts.join(", ")})` : "";
			return `"${c.title || 'Untitled'}"${typeStr}`;
		}).filter(Boolean);

		const sectionNames = Object.values(sectionStore).map(s => `"${s?.title || 'Untitled'}"`).filter(Boolean);

		let details = "";
		if (contentNames.length > 0 && sectionNames.length > 0) {
			details = `Saved sections: ${sectionNames.join(", ")} & content: ${contentNames.join(", ")}`;
		} else if (contentNames.length > 0) {
			details = `Saved content: ${contentNames.join(", ")}`;
		} else if (sectionNames.length > 0) {
			details = `Saved sections: ${sectionNames.join(", ")}`;
		}

		if (details) {
			logCourseAction(app.id, "Updated Course Content", details);
		}

		try {
			await Promise.all(apiRequests)
			toast({
				title: "Changes saved",
				description: "Your course changes have been saved successfully.",
				status: "success",
				duration: 3500,
				isClosable: true,
				position: "top-right"
			})

			const updatedOriginals: Record<string, string> = {};
			Object.entries(contentStore).forEach(([id, content]) => {
				if (id && id !== 'undefined' && id !== 'null') {
					updatedOriginals[id] = JSON.stringify(createServerContent(content));
				}
			});
			setOriginalContents(updatedOriginals);

			setSectionStore({});
			setDeletedContent([]);
			setDeletedSections([]);
			setStructureDirty(false);
			window.dispatchEvent(new CustomEvent('editor-dirty-state', { detail: { isDirty: false } }));
		} catch (err: any) {
			console.error("Save failed:", err);
			setProblem(err);
			toast({
				title: "Save failed",
				description: err?.response?.data?.message || err?.message || "There was a problem saving your changes. Please try again.",
				status: "error",
				duration: 5000,
				isClosable: true,
				position: "top-right"
			});
		} finally {
			popJob();
		}
	}

	let totalSections = 0;
	let totalVideos = 0;
	let totalDocs = 0;
	let totalOthers = 0;

	const countItems = (sections: NewSection[]) => {
		sections.forEach(s => {
			totalSections++;
			if (s.contents) {
				s.contents.forEach(item => {
					if (item.type === 'section') {
						countItems([item as NewSection]);
					} else if (item.type === 'video') {
						totalVideos++;
					} else if (item.type === 'pdf') {
						totalDocs++;
					} else {
						totalOthers++;
					}
				});
			}
		});
	};
	if (sectionMeta) {
		countItems(sectionMeta);
	}

	return (
		<SectionStoreContext.Provider value={[sectionStore, setSectionStore]}>
			<SetContentStoreContext.Provider value={setContentStore}>
				<ContentStoreContext.Provider value={[contentStore, setContentStore]}>
					<UploadJobsContext.Provider value={[pushJob, popJob]}>
						<SelectionReferenceContext.Provider value={[selectionReference, setSelectionReference]}>
							<EditorSyncContext.Provider value={[editorSync, setEditorSync]}>
								<ReferenceMediaContext.Provider value={referenceMedia}>
									<DereferenceMediaContext.Provider value={dereferenceMedia}>
										<DeletedContentContext.Provider value={[deletedContent, setDeletedContent]}>
											<DeletedSectionsContext.Provider value={[deletedSections, setDeletedSections]}>
												<ContentStructureContext.Provider value={[sectionMeta, setSectionMeta] as any}>
													<StructureDirtyContext.Provider value={[structureDirty, setStructureDirty]}>
														<NavCollapsedContext.Provider value={[isNavCollapsed, setIsNavCollapsed]}>
															<Flex w='100%' h='calc(100vh - 64px)' minH={0} position='relative'>
																<SectionNavigator />
																{selectionReference?.type === 'video' && <ContentEditor />}
																{selectionReference?.type === 'section' && <SectionEditor />}
																{selectionReference?.type === 'pdf' && <PdfEditor />}
																{selectionReference?.type === 'audio' && <AudioEditor />}
																{selectionReference?.type === 'quiz' && <QuizEditor />}
																{selectionReference?.type === 'image' && <ImageEditor />}
																{selectionReference?.type === 'flashcard' && <FlashcardEditor />}
																{!selectionReference && (
																	<Flex flex={1} flexDir="column" p={8} bg={useColorModeValue("gray.50", "gray.900")} justifyContent="center" alignItems="center" minH={0}>
																		<Center flexDir="column" p={12} maxW="600px" border="2px dashed" borderColor={useColorModeValue("gray.200", "gray.700")} borderRadius="2xl" bg={useColorModeValue("white", "gray.850")} shadow="sm" minH="250px">
																			<Box bg="green.50" _dark={{ bg: "rgba(56, 161, 105, 0.15)" }} p={4} borderRadius="full" color="green.500" mb={4}>
																				<FontAwesomeIcon icon={faVideo} size="2x" />
																			</Box>
																			<Heading size="md" mb={2} color={useColorModeValue("gray.700", "white")}>Select content to start editing</Heading>
																			<Text maxW="md" textAlign="center" color="gray.500" fontSize="sm" fontWeight="medium">
																				Click any section, quiz, flashcard, or video on the left sidebar tree to view and modify its annotations, options, and metadata.
																			</Text>
																		</Center>
																	</Flex>
																)}
															</Flex>
														</NavCollapsedContext.Provider>
													</StructureDirtyContext.Provider>
												</ContentStructureContext.Provider>
											</DeletedSectionsContext.Provider>
										</DeletedContentContext.Provider>
									</DereferenceMediaContext.Provider>
								</ReferenceMediaContext.Provider>
							</EditorSyncContext.Provider>
						</SelectionReferenceContext.Provider>
					</UploadJobsContext.Provider>
				</ContentStoreContext.Provider>
			</SetContentStoreContext.Provider>
		</SectionStoreContext.Provider>
	)
}

function findPath(sections: NewSection[], targetId: string, currentPath: string[] = []): string[] | null {
	for (const item of sections) {
		const newPath = [...currentPath, item.title];
		if (item.id === targetId) {
			return newPath;
		}
		if (item.contents && item.contents.length > 0) {
			for (const child of item.contents) {
				if (child.id === targetId) {
					return [...newPath, child.title];
				}
				if (child.type === 'section') {
					const result = findPath([child as NewSection], targetId, newPath);
					if (result) return result;
				}
			}
		}
	}
	return null;
}

function findContentItem(sections: NewSection[], targetId: string): NewContentItem | undefined {
	for (const item of sections) {
		if (item.id === targetId) {
			return item;
		}
		if (item.contents && item.contents.length > 0) {
			for (const child of item.contents) {
				if (child.id === targetId) {
					return child;
				}
				if (child.type === 'section') {
					const result = findContentItem([child as NewSection], targetId);
					if (result) return result;
				}
			}
		}
	}
	return undefined;
}
